import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MarketType, MatchStatus, getMarketPredictions } from "../../../../Utils";
import BonusFooter from "../BonusFooter";
import CommonPredictions from "../CommonPredictions";
import InfoHeader from "../InfoHeader";
import ScorePrediction from "../ScorePrediction";
import styles from "./PredictionCard.module.css";

// Memoize the InfoHeader to prevent logo flickering
const MemoizedInfoHeader = React.memo(InfoHeader);

// Create a separate component for the score section to isolate re-renders
const ScoreSection = React.memo(({ match, currentScore, onScoreSet, canMakePredictions }) => {
	// Memoize the score change handler
	const handleScoreChange = useCallback(
		(score) => {
			onScoreSet(match.id, score);
		},
		[match.id, onScoreSet],
	);

	return <ScorePrediction score={currentScore} match={match} onScoreSet={handleScoreChange} canMakePredictions={canMakePredictions} />;
});

// Create a separate component for the bonus section
const BonusSection = React.memo(({ match, markets, summary, userPredictions, onScoreSelected, canMakePredictions }) => {
	const { t } = useTranslation();
	const [marketsInfo, setMarketsInfo] = useState([]);

	// Update markets info
	useEffect(() => {
		setMarketsInfo(getMarketPredictions(markets, userPredictions));
	}, [markets, userPredictions]);

	const points = marketsInfo
		.map((market) => market.result)
		.filter((result) => result?.status === "SETTLED" || result?.status === "RESETTLED")
		.map((settledResult) => settledResult?.points)
		?.reduce((a, b) => a + b, 0);

	return (
		<div
			className={styles.predictionCardContainer}
			style={{
				paddingBottom: points >= 0 && match.status.type === "finished" ? "15px" : "0px",
			}}
		>
			{points >= 0 && match.status.type === "finished" && (
				<div className={styles.points}>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							margin: "0 15px",
							fontSize: "14px",
							borderRadius: "5px",
							overflow: "hidden",
							backgroundColor: points > 0 ? "rgba(0, 135, 0, 0.05)" : "rgba(153, 153, 153, 0.1)",
						}}
					>
						<div style={{ fontWeight: 700, padding: "10px" }}>
							{match.status.type === MatchStatus.FINISHED && (
								<>
									{t("predictions.predictionCard.result")}: {`${match.scores.ftScore.homeGoals}-${match.scores.ftScore.awayGoals}`}
								</>
							)}
						</div>
						<div
							style={{
								fontWeight: 700,
								color: "#fff",
								padding: "10px",
								backgroundColor: points > 0 ? "rgba(0, 135, 0, 1)" : "rgba(153, 153, 153, 1)",
							}}
						>{`+${points} ${t("predictions.predictionCard.pts")}`}</div>
					</div>
				</div>
			)}
			{Object.values(summary?.[0]?.predictions?.correctScore ?? {}).length > 0 && (
				<CommonPredictions
					match={match}
					scorePredictions={summary[0].predictions.correctScore}
					userPrediction={userPredictions.find((prediction) => prediction.market === MarketType.CORRECT_SCORE_ADVANCED)?.prediction}
					onScoreSelected={onScoreSelected}
					canMakePredictions={canMakePredictions}
				/>
			)}
			<BonusFooter match={match} markets={marketsInfo} />
		</div>
	);
});

const PredictionCard = ({ markets, match, summary, userPredictions, currentScore, onScoreSet, fansUnitedSDK, canMakePredictions }) => {
	const onScoreSelected = useCallback(
		(score) => {
			// Convert the score object to the format expected by onScoreSet
			onScoreSet(match.id, score);
		},
		[match.id, onScoreSet],
	);

	return (
		<div className={styles.container}>
			<MemoizedInfoHeader match={match} fansUnitedSDK={fansUnitedSDK} />
			<div className={styles.separatorTop} />
			<ScoreSection match={match} currentScore={currentScore} onScoreSet={onScoreSet} canMakePredictions={canMakePredictions} />
			{!match.undecided && (
				<BonusSection
					match={match}
					markets={markets}
					summary={summary}
					userPredictions={userPredictions}
					onScoreSelected={onScoreSelected}
					fansUnitedSDK={fansUnitedSDK}
					canMakePredictions={canMakePredictions}
				/>
			)}
		</div>
	);
};

export default PredictionCard;
